import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { CouponCode } from "./couponCode";
import {
  CouponCreateType,
  IConditionIdsItem,
  ICreateCouponWithExistingConditionsForm,
  IEventIdsItem,
  IPromotionConditionDetail,
  IPromotionEventIdsItem,
  UserType,
} from "interfaces";
import { HttpError, useCreate, useOne } from "@refinedev/core";
import { CreateCouponWithExistingConditionsSchema } from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Label } from "components/label";
import Select from "components/select";
import { useState } from "react";
import { endpointService } from "components/shared/services/endpointService";
import { AdditionalSetup } from "./additionalSetup";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CouponAlreadyExistsModal } from "../../modal";
import { get, isEmpty } from "lodash";
import { canAccessByType } from "utills/helpers";
import { ViewConditionSetting } from "../../condition/detail";

const INITIAL_DUPLICATE_CODE_MODAL_CONFIG: any = {
  open: false,
  data: null,
};

export const CreateCouponWithExistingConditions = () => {
  const { mutate, isLoading: isLoading } = useCreate();
  const navigate = useNavigate();
  const [duplicateCodeModalConfig, setDuplicateCodeModalConfig] = useState(
    INITIAL_DUPLICATE_CODE_MODAL_CONFIG
  );

  const methods = useForm<
    ICreateCouponWithExistingConditionsForm,
    HttpError,
    ICreateCouponWithExistingConditionsForm
  >({
    defaultValues: {
      condition_id: "",
      coupons: {
        create_type: CouponCreateType.MANUAL,
        is_active: true,
        max_times_used: 1,
        displayed_on_actiup: false,
        displayed_on_whitelabel: false,
        code_info_list: [{ code: "", customer_info_value: null }],
        customer_info_type: null,
        short_desc_en: "",
        short_desc_vi: "",
      },
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        CreateCouponWithExistingConditionsSchema()
      );
      return createResolver(values, context, options);
    },
  });
  const [eventId, setEventId] = useState("all");
  const [isTriggerPromotionEvents, setIsTriggerPromotionEvents] = useState(false);

  const { data: promotionEventData, isFetching: isFetchingPromotionEvents } = useOne<
    IPromotionEventIdsItem[],
    HttpError
  >({
    resource: endpointService.getPromotionEndPoint("getPromotionEventDropdown"),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!isTriggerPromotionEvents
    } as any
  });

  const { data: eventsData, isFetching: isFetchingEventIds } = useOne<
    IEventIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint(
      "getPromotionEventPercentFixedRuleDropdown"
    ),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const { data: conditionData, isFetching: isFetchingConditionIds } = useOne<
    IConditionIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint("getConditionDropdown", {
      eventId: eventId,
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: true,
    } as any,
  });

  const { data: conditionDetail } = useOne<IPromotionConditionDetail>({
    resource: endpointService.getPromotionEndPoint("getConditionDetail", {
      conditionId: methods.watch("condition_id"),
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!methods.watch("condition_id"),
    } as any,
  });

  const eventIds = [{ id: "all", name_en: "All", name_vi: "Tất cả" }].concat((eventsData?.data ?? [])).map((event: any) => ({
    value: event.id,
    label: event.name_en,
  }));

  const conditionIds = (conditionData?.data ?? []).map(
    (condition: IConditionIdsItem) => ({
      value: String(condition.id),
      label: condition.note,
    })
  );

  const conditionDetailData = conditionDetail?.data ?? null;

  const codeFieldArrController = useFieldArray({
    control: methods.control,
    name: "coupons.code_info_list",
  });

  const _onCloseDuplicateCodeModal = () => {
    setDuplicateCodeModalConfig({
      ...INITIAL_DUPLICATE_CODE_MODAL_CONFIG,
      data: duplicateCodeModalConfig.data,
    });
  };

  const onSubmit = (data: ICreateCouponWithExistingConditionsForm) => {
    const submitValues = { ...data };

    mutate(
      {
        resource: endpointService.getPromotionEndPoint(
          "createPromotionCouponWithExistingCondition"
        ),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Create successfully");
          navigate("/promotion/coupon");
        },
        onError: (error) => {
          const conflictCodes = get(error, "response.data.error.conflicts", []);
          if (!isEmpty(conflictCodes)) {
            if (!isTriggerPromotionEvents) {
              setIsTriggerPromotionEvents(true);
            }
            setDuplicateCodeModalConfig({
              open: true,
              data: conflictCodes,
            });
          }
        },
      }
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full h-full">
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="box-container">
            <div className="flex flex-col gap-y-4 md:flex-row md:items-start md:gap-x-4">
              <div className="flex-1">
                <Label id="event_id" name="Event Name" />
                <div className="mt-2">
                  <Select
                    key={`eventId__${eventId}`}
                    options={eventIds}
                    id="event_id"
                    isLoading={isFetchingEventIds}
                    placeholder="Select Event"
                    value={eventIds.find((d) => d.value === eventId)}
                    onChange={(selected: { label: string; value: string }) => {
                      setEventId(selected?.value);
                      methods.resetField("condition_id");
                    }}
                  />
                </div>
              </div>
              <div className="flex-1">
                <Label id="condition_id" name="Condition Name" />
                <div className="mt-2">
                  <Controller
                    control={methods.control}
                    name="condition_id"
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <Select
                          key={`conditionId__${eventId}`}
                          inputRef={ref}
                          options={conditionIds}
                          id="condition_id"
                          isLoading={isFetchingConditionIds}
                          placeholder="Select Condition"
                          defaultValue={
                            conditionIds.find(
                              (selected: { label: string; value: any }) =>
                                selected.value === value
                            ) ?? ""
                          }
                          value={conditionIds.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          )}
                          onChange={(selected: {
                            label: string;
                            value: any;
                          }) => {
                            onChange(selected?.value);
                          }}
                          error={methods.formState.errors["condition_id"]}
                        />
                      );
                    }}
                  />
                  {methods.formState.errors["condition_id"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {methods.formState.errors["condition_id"].message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {conditionDetailData && methods.watch("condition_id") ? (
            <>
              <ViewConditionSetting conditionDetailData={conditionDetailData} />
              <CouponCode codeFieldArrController={codeFieldArrController} />
              {canAccessByType(UserType.ADMIN) && (
                <AdditionalSetup conditionDetailData={conditionDetailData} />
              )}
            </>
          ) : (
            <></>
          )}

          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/promotion/coupon`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
          {isLoading ? <FullScreenLoading /> : <></>}
        </div>
      </form>
      <CouponAlreadyExistsModal
        open={duplicateCodeModalConfig.open}
        couponData={duplicateCodeModalConfig.data}
        events={promotionEventData?.data ?? []}
        onClose={_onCloseDuplicateCodeModal}
      />
    </FormProvider>
  );
};
